import { Injectable, inject } from '@angular/core';
import { Socket, io } from 'socket.io-client';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { commonFeature } from '../shared/store/reducers';
import * as TicketsActions from '../pages/tickets/store/actions';
import * as ChatsActions from '../pages/chats-history/store/actions';
import { Ticket, TicketSearchRequest } from '../types/interfaces/ticket';
import { ticketsFeature } from '../pages/tickets/store/reducers';
import {
  ChatMessage,
  MessageReactionsAttributes,
} from '../types/interfaces/message';
import { User } from '../types/interfaces/user';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../environments/environment';
import { v4 } from 'uuid';
import { MediaUploadService } from './media-upload.service';
import {
  ChatNewConnectionRequest,
  ChatNewManualConnectionRequest,
} from '../types/interfaces/message-templates';
import { SoundService } from './sound.service';
import {
  SupportAgentEvents,
  SupportServerEvents,
} from '../types/enums/agent-socket-events';
import { HttpClient, HttpParams } from '@angular/common/http';
import { logout } from '../shared/store/actions';
import { TicketStatusEnum } from '../types/enums/TicketStatusEnum';
import { Chat } from '../types/interfaces/chat';
import { normalizeTicketSearchRequest } from '../pages/tickets/ticket-search-request';

@Injectable({
  providedIn: 'root',
})
export class TicketsService {
  private store = inject(Store);
  private snackbar = inject(MatSnackBar);
  private mediaUploadService = inject(MediaUploadService);
  private soundService = inject(SoundService);
  private http = inject(HttpClient);

  get connected$(): Observable<boolean> {
    return this._connected$.asObservable();
  }

  private socket!: Socket;
  private _connected$ = new BehaviorSubject<boolean>(false);

  user!: User;
  ticketSelected: Ticket | undefined;

  private destroy$!: Subject<void>;

  init() {
    this.destroy$ = new Subject<void>();
    this.store
      .select(commonFeature.selectUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (!user) {
          return;
        }

        this.user = user;
      });

    this.store
      .select(ticketsFeature.selectTicketSelected)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ticket) => (this.ticketSelected = ticket));
  }

  cleanup() {
    this.destroy$.next();
    this.destroy$.complete();
    this.socket.disconnect();
    console.log('TicketsService cleanup()');
  }

  startConnection() {
    try {
      this.socket = io(`${environment.socketHost}/agents`, {
        autoConnect: false,
        withCredentials: true,
        transports: ['websocket'],
      });
      this.registerEventHandlers();
      this.socket.connect();
    } catch (ex) {
      console.error(ex);
    }
  }

  registerEventHandlers() {
    // this.socket.onAny((...args) => {
    //   console.log(args);
    // });
    this.socket.on(SupportAgentEvents.CONNECTION, () => {
      this._connected$.next(true);
    });
    this.socket.on(SupportAgentEvents.DISCONNECTION, () => {
      this._connected$.next(false);
    });
    this.socket.on(SupportServerEvents.INITIAL_TICKETS, (tickets) => {
      this.store.dispatch(TicketsActions.initialTickets({ tickets }));
    });
    this.socket.on(SupportServerEvents.NEW_TICKET, (ticket) => {
      this.store.dispatch(TicketsActions.newTicket({ ticket }));
      this.soundService.newTicketSound();
    });
    this.socket.on(SupportServerEvents.TICKET_UPDATED, (ticket) => {
      this.store.dispatch(TicketsActions.updateTicket({ ticket }));
    });
    this.socket.on(SupportServerEvents.TICKET_REMOVED, (ticketId) => {
      this.store.dispatch(TicketsActions.removeTicket({ ticketId }));
    });
    this.socket.on(SupportServerEvents.TICKET_ASSIGNED, (ticket) => {
      this.store.dispatch(TicketsActions.ticketAssigned({ ticket }));
    });
    this.socket.on(
      SupportServerEvents.NEW_USER_MESSAGE,
      (idTicket: number, message: ChatMessage) => {
        this.store.dispatch(TicketsActions.newMessage({ idTicket, message }));
        this.soundService.newMessageSound();
      }
    );
    this.socket.on(SupportServerEvents.OPERATION_ERROR, (error) => {
      this.snackbar.open(error);
    });
    this.socket.on(
      SupportServerEvents.MESSAGE_UPDATED,
      (message: ChatMessage) => {
        this.onMessageUpdated(message);
      }
    );
    this.socket.on(
      SupportServerEvents.CHAT_MESSAGES,
      (data: { chatId: number; messages: ChatMessage[] }) => {
        this.store.dispatch(TicketsActions.selectTicketMessages(data));
        this.store.dispatch(ChatsActions.selectChatMessages(data));
      }
    );
    this.socket.on(SupportServerEvents.NEW_SESSION_CONNECTED, () => {
      this.snackbar.open('Nueva sesión iniciada en otro dispositivo');
      this.store.dispatch(logout());
    });
    this.socket.on(
      SupportServerEvents.NEW_USER_REACTION,
      (idTicket: number, reaction: MessageReactionsAttributes) => {
        this.store.dispatch(TicketsActions.newReaction({ idTicket, reaction }));
        this.soundService.newMessageSound();
      }
    );
  }

  stopConnection() {
    console.log('DISCONNECT');
    this.socket!.disconnect();
  }

  changeTicketQueue(ticketId: number, queueId: number) {
    this.socket.emit(SupportAgentEvents.CHANGE_TICKET_QUEUE, ticketId, queueId);
  }

  acceptTicket(ticket: Ticket) {
    this.socket.emit(
      SupportAgentEvents.ACCEPT_TICKET,
      ticket.id,
      ticket.idChat
    );
  }

  changeTicketStatus(ticketId: number, statusId: number) {
    this.socket.emit(
      SupportAgentEvents.CHANGE_TICKET_STATUS,
      ticketId,
      statusId
    );

    if (
      statusId === TicketStatusEnum.CLOSED &&
      ticketId === this.ticketSelected?.id
    ) {
      this.store.dispatch(TicketsActions.selectTicket({ ticket: undefined }));
    }
  }

  sendMessage(message: ChatMessage) {
    if (!this.ticketSelected) {
      console.log('No ticket selected');
      return;
    }

    message.senderId = String(this.user.id);
    message.sender = this.user.name;

    if (!message.tempId) {
      message.tempId = v4();
    }

    this.store.dispatch(
      TicketsActions.newAgentMessage({
        idTicket: this.ticketSelected.id!,
        message: message,
      })
    );

    this.socket.emit(
      SupportAgentEvents.NEW_MESSAGE,
      this.ticketSelected.id,
      message
    );
  }

  onMessageUpdated(message: ChatMessage) {
    this.store.dispatch(TicketsActions.updateMessage({ message }));

    if (message.type == 'media' && message.tempId) {
      // start upload of pending file
      this.mediaUploadService.queueUploadById(message.tempId, message.id);
    }
  }

  openNewChatConnection(ticketId: number, req: ChatNewConnectionRequest) {
    this.socket.emit(
      SupportAgentEvents.OPEN_TICKET_CONVERSATION,
      ticketId,
      req
    );
  }

  getChatMessages(idChat: number) {
    this.socket.emit(SupportAgentEvents.GET_CHAT_MESSAGES, idChat);
  }

  markTicketAsRead(ticket: Ticket, lastMessageId: number): void {
    this.socket.emit(
      SupportAgentEvents.MARK_TICKET_AS_READ,
      ticket.id,
      ticket.idChat,
      lastMessageId
    );
  }

  reOpenTicket(ticketId: number) {
    this.socket.emit(SupportAgentEvents.REOPEN_TICKET, ticketId);
  }

  createTicket(req: ChatNewManualConnectionRequest) {
    const formData = new FormData();
    formData.append('json', JSON.stringify({ ...req, files: undefined }));

    if (req.files) {
      for (let file of req.files) {
        formData.append(file.tempId, file.file, file.file.name);
      }
    }

    return this.http.post(`${environment.apiHost}/tickets`, formData, {
      withCredentials: true,
    });
  }

  getHistoryTickets(data: TicketSearchRequest) {
    let params = new HttpParams();
    const normalizedData = normalizeTicketSearchRequest(data);

    Object.entries(normalizedData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null) {
              params = params.append(key, item.toString());
            }
          });
        } else {
          params = params.set(key, value.toString());
        }
      }
    });

    return this.http.get<Ticket[]>(`${environment.apiHost}/tickets/history`, {
      params,
      withCredentials: true,
    });
  }

  getHistoryChats(data: TicketSearchRequest) {
    let params = new HttpParams();

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null) {
              params = params.append(key, item.toString());
            }
          });
        } else {
          params = params.set(key, value.toString());
        }
      }
    });

    return this.http.get<Chat[]>(
      `${environment.apiHost}/tickets/chats-history`,
      {
        params,
        withCredentials: true,
      }
    );
  }

  selectTicket(ticket: Ticket) {
    const deselecting = ticket.id === this.ticketSelected?.id;

    this.store.dispatch(TicketsActions.selectTicket({ ticket }));

    if (deselecting) {
      console.log('Deselecting ticket');
      return;
    }

    this.getChatMessages(ticket.idChat);

    // Optimistically update the ticket
    this.store.dispatch(
      TicketsActions.markTicketAsRead({
        agentId: this.user.id!,
        ticketId: ticket.id!,
        lastMessageId: 0,
      })
    );
  }

  selectChat(chat: Chat) {
    this.store.dispatch(ChatsActions.selectChat({ chat }));
    this.getChatMessages(chat.idChat);
  }
}
